import type { MarketingSectionId } from '../../../context/TenantContext';

/**
 * Anchor per section.
 *
 * Sections with no entry simply don't get one — that is the normal case for
 * hero and the closing CTA, which people reach by scrolling.
 */
export const REVISE_ANCHORS: Partial<Record<MarketingSectionId, string>> = {
  hero: 'top',
  labStudy: 'study',
  labEssay: 'essay',
  outcomes: 'comes-back',
  labPractice: 'practice',
  bundles: 'bundles',
  courses: 'browse',
  gifts: 'free',
  pricing: 'pricing',
  credits: 'who',
  cta: 'start-free',
};

/**
 * Nav links, in nav order, which follows page order.
 *
 * Each carries its OWN anchor rather than reusing the section's, because the
 * first link points at the try-it-here block *inside* the Study Labs section
 * rather than at the top of it — the demo is the hook, so it gets the first
 * link and the section it lives in gets the second. `id` names the section
 * that has to be rendered for the link to be live, which is how a removed
 * section takes its nav entries with it.
 */
export const REVISE_NAV: ReadonlyArray<{
  id: MarketingSectionId;
  anchor: string;
  label: string;
}> = [
  { id: 'labStudy', anchor: 'try', label: 'Try a lesson' },
  { id: 'labStudy', anchor: 'study', label: 'Study Labs' },
  { id: 'labEssay', anchor: 'essay', label: 'Essay Labs' },
  // Named for the thing, not the offer. "Free NSC papers" described the price
  // and left the reader to work out what they'd be opening; the section it
  // lands on is the Practice Labs, and the announce bar above already carries
  // "free" as the offer.
  { id: 'labPractice', anchor: 'practice', label: 'Practice Labs' },
];

/**
 * The masthead on `/ieb`.
 *
 * The NSC nav above is anchors into the LANDING page, so from the IEB page
 * every one of them was a door out of the board the reader had just chosen —
 * "Study Labs" on the IEB page took an IEB student to the NSC Study Labs
 * (Dylan, Sept 2026). These are that page's own sections instead, and the
 * board link flips from IEB to NSC, so each board's masthead offers its own
 * contents plus one clearly-labelled way across.
 *
 * Plain anchors, not section ids: this page is not composed from the tenant's
 * section list, so there is nothing to filter against — the sections are
 * always all there.
 */
export const REVISE_IEB_NAV: ReadonlyArray<{ anchor: string; label: string }> = [
  /* The demo first, for the reason it is first in the NSC nav: it is the
     hook, and it lives inside the Study Labs section rather than being one. */
  { anchor: 'try', label: 'Try a lesson' },
  { anchor: 'labs', label: 'Study Labs' },
  { anchor: 'skills', label: 'Skills Labs' },
  { anchor: 'essay', label: 'Essay Labs' },
  { anchor: 'pricing', label: 'Pricing' },
];
